/* OnSumn — Homepage Venue Rail
 *
 * "Rooms We Watch" rail of the spine venues. Renders chips that link to each
 * /venue/[slug]. Each chip carries a city tag, the venue short name, and an
 * optional VenueHeatPill (cold venues stay clean — pill returns null when
 * heat is silent per Mav's spec).
 *
 * Spec: Zora — locked + Marco-signed-off. Reza synthesis adjustment: reuse
 * the shipped VenueHeatPill component at chip-scale for social-proof signal.
 *
 * Pattern: window-attached function component, React via window.React,
 * shared hook window.useReducedMotion(), heat via window.useVenueHeat().
 *
 * Cleanup 2026-09-02 (Zora, docs/design/2026-09-02-rooms-we-watch-cleanup-SPEC.md):
 * fixed-count grid on desktop, 2-row strip on mobile, a reserved heat slot,
 * the bare pill, light-mode ink tokens, a capped stagger. Order, material,
 * type sizes and the CLS-safe isMobile initializer are untouched.
 */

// The ONE city display map (lib/city-registry.js) — the same function the
// header rail renders its pills through. `city` in VENUE_RAIL below stays
// the CLAIM (canonical municipality, uppercase, untouched); the chip prints
// cityToTag(city). Ruling note on VenueChip's line 1.
import { cityToTag } from '../lib/city-registry.js';

// Venue spine — order matches SPINE_VENUES in VenueHeader.jsx for site-wide
// consistency. Hard-coded inside the component intentionally: this is the
// editorial picks rail, not a dynamic list.
const VENUE_RAIL = [
  { slug: 'the-fillmore',                  short: 'The Fillmore',  city: 'SAN FRANCISCO', place_id: 'ChIJO2EGsrmAhYARdDfDm-uVTQQ' },
  { slug: 'bill-graham-civic-auditorium',  short: 'Bill Graham',   city: 'SAN FRANCISCO', place_id: 'ChIJNwYP6ZuAhYAR-d1KDyXvWNU' },
  // Chase Center (added 2026-06-02 per Marco) — arena-scale waterfront room
  // in Mission Bay. Hosts Warriors games + big-room tours (A$AP, Joji,
  // Daniel Caesar, Diljit, Lionel Richie). Reads as the SF arena-tier
  // counterpart to Oakland Arena in the rail order.
  { slug: 'chase-center',                  short: 'Chase Center',  city: 'SAN FRANCISCO', place_id: 'ChIJ6QBEf8Z_j4AR40Gh2OOTZ30' },
  { slug: 'fox-theater-oakland',           short: 'Fox Theater',   city: 'OAKLAND',       place_id: 'ChIJoRvsEbKAj4ARvoIv-SRbyAc' },
  // Oakland Arena (added 2026-05-27) clusters with Fox Theater geographically —
  // both Oakland rooms read together in the rail. Arena scale + Ticketmaster
  // sourcing (no public calendar) — see scrapers/oakland-arena-bridge.js.
  { slug: 'oakland-arena',                 short: 'Oakland Arena', city: 'OAKLAND',       place_id: 'ChIJseireMOFj4AR5A3Q7Zw0l2Q' },
  // Levi's Stadium (added 2026-06-02 per Marco) — stadium-tier South Bay
  // room. Hosts Ed Sheeran / AC/DC / Karol G / Usher+Chris Brown tier
  // tours. Geographically the southernmost spine venue.
  { slug: 'levis-stadium',                 short: "Levi's Stadium",city: 'SANTA CLARA',   place_id: 'ChIJ3_DFJ8jJj4ARGH0fLjdaRRE' },
  { slug: 'frost-amphitheater',            short: 'Frost',         city: 'STANFORD',      place_id: 'ChIJyYFc8pu7j4AR2seBP2vmNyQ' },
  { slug: 'the-catalyst',                  short: 'The Catalyst',  city: 'SANTA CRUZ',    place_id: 'ChIJGccQ7idAjoARtn2rAwhfYrk' },
  { slug: 'sweetwater-music-hall',         short: 'Sweetwater',    city: 'MILL VALLEY',   place_id: 'ChIJMeT2cXKQhYARg-TMgrnI1L8' },
  // ROOMS WE WATCH marquee-7 (2026-07-20, Reza-ruled). Appended after the
  // original 9 (KEEP: existing chips byte-identical); SF rooms first, then
  // East Bay, then South Bay — mirrors SPINE_VENUES in VenueHeader.jsx.
  { slug: 'the-warfield',                  short: 'The Warfield',  city: 'SAN FRANCISCO', place_id: 'ChIJ26vXqYWAhYAR-pHMSmLA0nA' },
  { slug: 'the-masonic',                   short: 'The Masonic',   city: 'SAN FRANCISCO', place_id: 'ChIJgW2ng5KAhYARvkYbpumZda0' },
  { slug: 'the-regency-ballroom',          short: 'The Regency',   city: 'SAN FRANCISCO', place_id: 'ChIJH3E4qZWAhYARe4tjSWXQgSQ' },
  { slug: 'the-independent',               short: 'The Independent', city: 'SAN FRANCISCO', place_id: 'ChIJI3We0a-AhYARveSUnxtLX3Q' },
  { slug: 'greek-theatre-berkeley',        short: 'Greek Theatre', city: 'BERKELEY',      place_id: 'ChIJTemcGJl-hYARjJohd2uBRZI' },
  { slug: 'sap-center',                    short: 'SAP Center',    city: 'SAN JOSE',      place_id: 'ChIJq56TmaDMj4ARJJCpWy9v-i0' },
  { slug: 'shoreline-amphitheatre',        short: 'Shoreline',     city: 'MOUNTAIN VIEW', place_id: 'ChIJZeH1dve5j4ARPT9VG0oB3x0' },
  // Bottom of the Hill (Goal 2B, 2026-07-20) — Reza Tier-1 DIY room, the
  // first bespoke-adapter spine venue. Rides at the end of the SF cluster
  // order-wise; mirrors SPINE_VENUES in VenueHeader.jsx.
  { slug: 'bottom-of-the-hill',            short: 'Bottom of the Hill', city: 'SAN FRANCISCO', place_id: 'ChIJQcdLBsx_j4AROyDSIMBYKH8' },
  // The Chapel + GAMH (Goal 2A, 2026-07-20) — SeeTickets-adapter pair;
  // the tastemaker rooms Reza's ruling weighted the rail toward.
  { slug: 'the-chapel',                    short: 'The Chapel',    city: 'SAN FRANCISCO', place_id: 'ChIJzdMHHj1-j4ARCU4ZzZw5lBg' },
  { slug: 'great-american-music-hall',     short: 'Great American', city: 'SAN FRANCISCO', place_id: 'ChIJI56vzpaAhYARuopEz-SlLuU' },
  // The Midway (2026-08-27) — Dogpatch warehouse complex, the electronic
  // room. Appended at the end of the SF cluster; mirrors SPINE_VENUES in
  // VenueHeader.jsx. The 'NN ROOMS' count reads VENUE_RAIL.length, so it
  // follows this line without a second edit.
  { slug: 'the-midway',                    short: 'The Midway',    city: 'SAN FRANCISCO', place_id: 'ChIJ0Xb_IqR_j4ARoBy8BX7TMCE' },
];

// Mobile breakpoint mirrors the rest of the homepage chrome (≤768).
function useIsMobile() {
  // Read during render — see Header.jsx and lib/breakpoints.js#isBelow. The
  // old useState(false) made ROOMS WE WATCH paint its desktop grid first
  // (x=32 w=326 h=295) then collapse to the mobile rail (x=0 w=390 h=103),
  // which was the largest single contributor to homepage CLS.
  const [isMobile, setIsMobile] = React.useState(
    () => (typeof window !== 'undefined' && window.bp ? window.bp.isMobile() : false),
  );
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    // Canonical `md` breakpoint (lib/breakpoints.js). The earlier
    // `(max-width: 768px)` was overlap-inclusive — switched to the
    // bp helper which subtracts 0.02px so this and any future
    // min-width('md') don't both fire at exactly 768.
    const mq = (window.bp && window.bp.maxWidth)
      ? window.bp.maxWidth('md')
      : window.matchMedia('(max-width: 767.98px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);
  return isMobile;
}

// Single chip — anchor with city tag + venue short name + optional heat pill.
function VenueChip({ venue, darkMode, radius, heat, index, visible, reducedMotion }) {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);

  // Hover affordance is desktop-only — guarded by @media (hover: hover) in
  // CSS world. JS world: still safe to set on focus, and `hovered` is only
  // toggled by mouse events which mobile browsers don't fire reliably.
  const active = hovered || focused;

  // Color tokens — verbatim from Zora's locked spec.
  const cityColorDefault = darkMode ? 'var(--fog-blue-light)' : 'var(--fog-blue-deep)';
  // Light active ink is --inverse-primary (#8E4E00, 6.4:1) — --amber-gold
  // read 3.2:1 on the active light chip.
  const cityColorActive = darkMode ? 'var(--amber-gold-light)' : 'var(--inverse-primary)';
  const nameColor = darkMode ? '#FFFFFF' : '#0A0A0A';
  const bgDefault = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.55)';
  const bgActive = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.85)';
  const borderDefault = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const borderActive = '1px solid var(--amber-gold)';

  // Mount-in animation stagger, capped at 8 steps: at index*60 the 20th chip
  // began rising 1.14s after the section was visible and read as lag. 0…360ms
  // lands every chip within 780ms.
  const animDelay = `${Math.min(index, 8) * 45}ms`;
  const animTransform = visible || reducedMotion ? 'translateY(0)' : 'translateY(8px)';
  const animOpacity = visible || reducedMotion ? 1 : 0;

  // Hover transform — omit translateY when reduced motion is on.
  const hoverTransform = active && !reducedMotion ? 'translateY(-1px)' : 'translateY(0)';

  // Compose transform: mount-in OR hover lift (mount wins until visible).
  const composedTransform = visible || reducedMotion ? hoverTransform : animTransform;

  return (
    <a
      href={`/venue/${venue.slug}`}
      className="focus-double-ring"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        alignItems: 'flex-start',
        textDecoration: 'none',
        padding: '10px 14px',
        minHeight: 44,
        borderRadius: Math.max(radius - 4, 10),
        background: active ? bgActive : bgDefault,
        backdropFilter: 'blur(12px) saturate(140%)',
        WebkitBackdropFilter: 'blur(12px) saturate(140%)',
        border: active ? borderActive : borderDefault,
        // Mount-in: opacity + 8→0px rise; staggered by index. Hover lift only
        // applies once visible — mount-in takes precedence to avoid jitter.
        opacity: animOpacity,
        transform: composedTransform,
        transition: reducedMotion
          ? 'background 180ms, border-color 180ms, color 180ms'
          : `opacity 420ms cubic-bezier(0.16, 1, 0.3, 1) ${animDelay},
             transform 420ms cubic-bezier(0.16, 1, 0.3, 1) ${animDelay},
             background 180ms, border-color 180ms, color 180ms`,
        // Mobile scroll-snap.
        scrollSnapAlign: 'start',
      }}
    >
      {/* Line 1 — city tag. VOCABULARY RULING (Rhea, 2026-09-02; Zora's
        * cleanup spec §1 MEDIUM): the chip prints the header rail's declared
        * strings via cityToTag() — SF for San Francisco, every other city its
        * own name (OAKLAND, not OAK and not the retired THE TOWN; BERKELEY;
        * SAN JOSE; MILL VALLEY …). One map, no second vocabulary here:
        * "SAN FRANCISCO" ×11 was the most repeated element on the homepage,
        * 300px under a nav pill that says SF. Gate:
        * tests/venue-rail-city-tag.test.js. */}
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        lineHeight: 1,
        color: active ? cityColorActive : cityColorDefault,
        transition: 'color 180ms',
      }}>{cityToTag(venue.city)}</span>

      {/* Line 2 — venue short name (proper noun, NOT uppercase) */}
      <span style={{
        fontFamily: 'var(--font-display)',
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1,
        color: nameColor,
      }}>{venue.short}</span>

      {/* Line 3 — heat slot, ALWAYS rendered at a fixed 16px so a chip is
        * 69px tall with or without a pill. The heat fetch lands ~200ms after
        * first paint; a slot that only existed once it arrived grew every row
        * 20px post-paint, and a room whose heat went silent sat shorter than
        * its neighbours. A cold room (pill returns null per Mav's spec) gets
        * 16px of air, no placeholder — the UI cannot tell "cron hasn't run"
        * from "one lonely show", and cold rooms stay clean. */}
      <span style={{ display: 'flex', alignItems: 'center', height: 16 }}>
        {typeof window !== 'undefined' && window.VenueHeatPill && (
          <window.VenueHeatPill placeId={venue.place_id} heat={heat} bare />
        )}
      </span>
    </a>
  );
}

function VenueRailSection({ darkMode, radius }) {
  const isMobile = useIsMobile();
  const reducedMotion = typeof window !== 'undefined' && typeof window.useReducedMotion === 'function'
    ? window.useReducedMotion()
    : false;
  // Heat data — shared singleton fetcher from Events.jsx. Returns {} when
  // not yet fetched; VenueHeatPill safely renders null until it arrives.
  const heat = typeof window !== 'undefined' && typeof window.useVenueHeat === 'function'
    ? window.useVenueHeat()
    : {};

  // IntersectionObserver — fire once at 20% visible, then disconnect.
  // Reduced-motion users skip the observer entirely (render at final state).
  const sectionRef = React.useRef(null);
  const [visible, setVisible] = React.useState(reducedMotion);
  React.useEffect(() => {
    if (reducedMotion) { setVisible(true); return; }
    if (!sectionRef.current || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { threshold: 0.2 });
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [reducedMotion]);

  // Chip row layout. Mobile = 2-row column-flow strip: 10 column pairs
  // (~1,420px, was a 2,668px single row), four whole chips visible on a 375
  // phone, reading order down-then-right = DOM order; scroll-snap + hidden
  // scrollbar as before. Desktop = fixed-count grid, 4 columns at md and 5
  // at lg+ (rules in the scoped <style> below). Only 4, 5 and 10 divide 20,
  // so there is never an orphan row — flex-wrap gave 9/9/2 at 1280 — and
  // tiles stretch to the cell so every column edge lines up.
  const chipRowStyle = isMobile
    ? {
        display: 'grid',
        gridAutoFlow: 'column',
        gridTemplateRows: 'repeat(2, auto)',
        gridAutoColumns: 'max-content',
        gap: 12,
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        scrollPaddingInline: 20,
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        paddingInline: 20,
        paddingInlineEnd: 40,
        // Negative margin pulls the chip strip out to the section edge so
        // padding can hint scrollability past the right edge.
        marginInline: 'calc(clamp(20px, 4vw, 32px) * -1)',
      }
    : {
        display: 'grid',
        gap: 12,
      };

  return (
    <section
      ref={sectionRef}
      data-venue-rail
      role="navigation"
      aria-labelledby="venue-rail-title"
      style={{
        position: 'relative',
        // Match the events-section rhythm (EventsMapSection, Map.jsx — up to
        // '80px 32px' at desktop) but trimmed on top since the hero already
        // ends with breathing room above this section. Bottom keeps room
        // before the events section's top padding.
        padding: isMobile ? '32px 16px 48px' : '48px 32px 64px',
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Magazine section header — leading bar + uppercase Be Vietnam +
          * rule extending to the right edge. Mirrors SectionHeader in
          * VenueEventList.jsx so the rail reads as part of the site's
          * editorial system. currentColor inherits theme automatically. */}
        <h2 style={{
          fontFamily: "'Be Vietnam Pro', sans-serif",
          fontWeight: 800,
          fontSize: 'clamp(18px, 4.6vw, 22px)',
          lineHeight: 1.1,
          textTransform: 'uppercase',
          letterSpacing: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          margin: '0 0 24px',
          color: 'var(--on-surface)',
        }}>
          <span style={{
            width: 24,
            height: 2,
            background: 'currentColor',
            flexShrink: 0,
          }} aria-hidden="true" />
          <span id="venue-rail-title">Rooms We Watch</span>
          <span style={{
            flex: 1,
            height: 1,
            background: 'currentColor',
            opacity: 0.2,
          }} aria-hidden="true" />
          <span style={{
            // Inventory count tucked at the end of the rule — present but
            // never competing with the header. tabular-nums keeps the
            // leading zero aligned across themes. Count derives from
            // VENUE_RAIL.length so the label can't drift when a spine
            // venue is added or removed.
            fontFamily: 'var(--font-body)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: 'var(--on-surface-muted)',
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}>{String(VENUE_RAIL.length).padStart(2, '0')} ROOMS</span>
        </h2>

        {/* Chip row */}
        <div data-rail-grid style={chipRowStyle}>
          {VENUE_RAIL.map((venue, i) => (
            <VenueChip
              key={venue.slug}
              venue={venue}
              darkMode={darkMode}
              radius={radius}
              heat={heat}
              index={i}
              visible={visible}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>
      </div>

      {/* Scoped CSS — the webkit scrollbar hide (scrollbar-width: none covers
        * Firefox inline) and the desktop grid columns. Columns live here, not
        * inline, because inline style has no media queries: 4 columns from
        * --bp-md, 5 from --bp-lg — the same 768 seam useIsMobile() reads, so
        * the JS row style and the CSS columns never disagree. */}
      <style>{`
        [data-venue-rail] ::-webkit-scrollbar { display: none; width: 0; height: 0; }
        @media (min-width: 768px) { [data-venue-rail] [data-rail-grid] { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
        @media (min-width: 1024px) { [data-venue-rail] [data-rail-grid] { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
      `}</style>
    </section>
  );
}

window.VenueRailSection = VenueRailSection;
